import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";

export const STATUS_WAITING_FOR_HUMAN_INPUT = "WAITING_FOR_HUMAN_INPUT";
export const STATUS_WAITING_FOR_FORM_SUBMISSION = "WAITING_FOR_FORM_SUBMISSION";
export const STATUS_WAITING_FOR_REVIEW = "WAITING_FOR_REVIEW";
export const STATUS_SESSION_UNDER_REVIEW = "SESSION_UNDER_REVIEW";

/** Display-only chip key — not a backend session status string. */
export const SESSION_CHIP_WAITING_FOR_HUMAN = "WAITING_FOR_HUMAN";

/**
 * Backend session statuses that share the single "Waiting for human" chip in session lists
 * and headers. The underlying status is preserved for composer/loop logic; only display
 * is consolidated.
 */
export const SESSION_STATUSES_WAITING_FOR_HUMAN = new Set([
  STATUS_WAITING_FOR_HUMAN_INPUT,
  STATUS_WAITING_FOR_FORM_SUBMISSION,
  STATUS_WAITING_FOR_REVIEW,
  STATUS_SESSION_UNDER_REVIEW,
]);

/** Maps a backend session status to the chip key used by {@link buildSessionStatusStyles}. */
export function resolveSessionStatusChipKey(status) {
  if (SESSION_STATUSES_WAITING_FOR_HUMAN.has(status)) {
    return SESSION_CHIP_WAITING_FOR_HUMAN;
  }
  return status;
}

export function buildSessionStatusStyles(p) {
  return {
    COMPLETED:   { ...p.green, label: "Completed" },
    ERROR:       { ...p.red, label: "Error" },
    MAX_ITERATIONS_REACHED: { ...p.amber, label: "Max Iterations" },
    IN_PROGRESS: { ...p.blue, label: "In Progress" },
    CANCELLED:   { ...p.zinc, label: "Cancelled" },
    [SESSION_CHIP_WAITING_FOR_HUMAN]: {
      ...p.purple,
      label: "Waiting for human",
    },
    WAITING_FOR_SCHEDULED_MESSAGE: { ...p.purple, label: "Scheduled" },
    HUMAN_TAKEOVER: { ...p.teal, label: "Human takeover" },
    WAITING_FOR_WORKERS: { ...p.purple, label: "Waiting for workers" },
  };
}

export const SIDEBAR_DEFAULT_WIDTH = 260;
export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 560;
export const CHAT_AREA_MIN_WIDTH = 420;
export const SIDEBAR_RESIZE_HANDLE_WIDTH = 6;
export const TASK_QUEUE_DEFAULT_WIDTH = 420;
export const TASK_QUEUE_MIN_WIDTH = 320;
export const TASK_QUEUE_MAX_WIDTH = 640;
export const TASK_QUEUE_RESIZE_HANDLE_WIDTH = 6;
export const SESSION_ROW_AVATAR_SIZE = 24;
export const SESSION_ROW_AVATAR_RADIUS = "6px";
export const DEFAULT_BLOCKED_WHILE_REVIEW_MESSAGE =
  "This conversation is currently under review. Please wait for the reviewer to finish.";
export const TASK_QUEUE_AUTO_OPEN_EVENT_TYPES = new Set([
  "task_queue_created",
  "task_started",
  "task_queue_completed",
  "task_queue_cancelled",
  "task_queue_recovered",
  "task_queue_paused_for_error",
]);

export function parsePublicErrorPayload(error) {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object") {
    return responseData;
  }
  if (typeof responseData === "string") {
    try {
      return JSON.parse(responseData);
    } catch (_error) {
      return null;
    }
  }
  return null;
}

export function parsePublicErrorMessage(error, fallback) {
  const payload = parsePublicErrorPayload(error);
  if (payload && typeof payload === "object") {
    return payload.error || payload.message || fallback;
  }
  const raw = error?.response?.data;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return fallback;
}

export function parseMaybeJsonPayload(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }
  if (typeof value === "object") return value;
  return null;
}

/**
 * Canonical session-list title truncation. Collapses whitespace and caps the
 * length so long prompts don't blow out the sidebar row. `fallback` is returned
 * for empty input (e.g. "New session" for optimistic rows). This is the single
 * source of truth for list titles so the optimistic and detail-derived paths
 * never produce divergent strings for the same session.
 */
export function truncateSessionTitle(content, fallback = "") {
  const text = String(content || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!text) return fallback;
  return text.length > 120 ? `${text.slice(0, 119)}…` : text;
}

/**
 * The effective display sender for a session-list row, preferring the
 * denormalized display name over the raw sender name.
 */
export function sessionListSenderName(session) {
  return session?.senderDisplayName || session?.senderName || "";
}

export function isSessionRowMetadataPending(session) {
  if (!session?.chatSessionId) return true;
  const hasTitle = Boolean(String(session.title || "").trim());
  const hasSender = Boolean(sessionListSenderName(session).trim());
  return !hasTitle && !hasSender;
}

export function isWorkflowProvenanceSession(session) {
  const source = session?.source;
  return (
    source === "WORKFLOW_INVOCATION" ||
    source === "WORKFLOW_INVOCATION_ASYNC" ||
    source === "WORKFLOW_OPERATOR_MANAGED_RUN"
  );
}

/**
 * True when a sender string names a real participant rather than the "System"
 * placeholder the backend emits before a session's sender is denormalized.
 * Centralizes the brittle `!== "System"` sentinel check shared across the
 * sidebar avatar logic and the optimistic session-list reconciliation.
 */
export function hasAuthoritativeSender(sender) {
  return Boolean(sender) && sender !== "System";
}

/**
 * Resolves the sidebar thread avatar label for a session row.
 * DM threads without denormalized sender fields fall back to the local user,
 * not "System". Skeleton state is only for in-flight optimistic rows.
 */
export function resolveSessionRowSender(session, localUserDisplayName = "") {
  const rawSender = sessionListSenderName(session);
  const hasRealSender = hasAuthoritativeSender(rawSender);

  if (hasRealSender) {
    return { senderName: rawSender, isHydrating: false };
  }

  if (session?._optimisticListRow === true) {
    return { senderName: rawSender, isHydrating: true };
  }

  if (isWorkflowProvenanceSession(session)) {
    return { senderName: rawSender || "System", isHydrating: false };
  }

  const localSender =
    typeof localUserDisplayName === "string" ? localUserDisplayName.trim() : "";
  if (localSender) {
    return { senderName: localSender, isHydrating: false };
  }

  return { senderName: rawSender || "You", isHydrating: false };
}

export function normalizeSessionListSenders(sessions, localUserDisplayName = "") {
  return (Array.isArray(sessions) ? sessions : []).map((session) => {
    const { senderName, isHydrating } = resolveSessionRowSender(
      session,
      localUserDisplayName
    );
    if (isHydrating || !senderName) return session;

    const currentSender = sessionListSenderName(session);
    if (hasAuthoritativeSender(currentSender)) return session;

    return {
      ...session,
      senderDisplayName: senderName,
      senderName,
    };
  });
}

/**
 * Single readiness signal for a session-list row, replacing the previously
 * split `isSessionRowMetadataPending` + `resolveSessionRowSender.isHydrating`
 * checks. `pending` rows lack any metadata and render a full-row skeleton;
 * `hydrating` rows are optimistic and render an avatar-only skeleton while the
 * sender denormalizes; `ready` rows render normally.
 */
export function sessionRowReadiness(session, localUserDisplayName = "") {
  if (isSessionRowMetadataPending(session)) {
    return { state: "pending", senderName: "" };
  }
  const { senderName, isHydrating } = resolveSessionRowSender(
    session,
    localUserDisplayName
  );
  return { state: isHydrating ? "hydrating" : "ready", senderName };
}

export function getApiStatusCode(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return 0;
  return Number(responseOrError?.status || responseOrError?.response?.status || 0);
}

export function getApiPayload(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return null;
  return parseMaybeJsonPayload(responseOrError?.data ?? responseOrError?.response?.data);
}

export function parseTaskQueueErrorMessage(responseOrError, fallbackMessage) {
  const payload = getApiPayload(responseOrError);
  if (payload && typeof payload === "object") {
    const queueError = payload?.error;
    if (queueError && typeof queueError === "object") {
      if (typeof queueError.message === "string" && queueError.message.trim()) {
        return queueError.message.trim();
      }
      if (typeof queueError.code === "string" && queueError.code.trim()) {
        return queueError.code.trim();
      }
    }
    if (typeof queueError === "string" && queueError.trim()) {
      return queueError.trim();
    }
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
  }
  if (
    typeof responseOrError?.message === "string" &&
    responseOrError.message.trim()
  ) {
    return responseOrError.message.trim();
  }
  return fallbackMessage;
}

export function queueUiIdempotencyKey() {
  return `task-queue-ui-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const PanelRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100%",
  minHeight: 0,
  width: "100%",
  minWidth: 0,
  backgroundColor: theme.palette.background.paper,
  overflowY: "hidden",
  overflowX: "auto",
}));

export const Sidebar = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.background.paper
      : theme.palette.background.subtle,
}));

export const SidebarList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.divider} transparent`,
}));

export const ChatArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflowY: "hidden",
  overflowX: "auto",
  backgroundColor: theme.palette.background.paper,
}));

export function normalizeChatUiPolicy(chatUiPolicy) {
  return {
    showSystemPromptInChat: chatUiPolicy?.showSystemPromptInChat !== false,
    showToolCallsAndOutputsInChat:
      chatUiPolicy?.showToolCallsAndOutputsInChat !== false,
    showInterAgentMessagesInChat:
      chatUiPolicy?.showInterAgentMessagesInChat !== false,
    showWorkflowProvenanceInChat:
      chatUiPolicy?.showWorkflowProvenanceInChat !== false,
    showSchedulingActivityInChat:
      chatUiPolicy?.showSchedulingActivityInChat !== false,
    showSessionOperationalMetaInChat:
      chatUiPolicy?.showSessionOperationalMetaInChat !== false,
    showSupervisorControlMessagesInChat:
      chatUiPolicy?.showSupervisorControlMessagesInChat !== false,
    showSharedSessionsSidebar:
      chatUiPolicy?.showSharedSessionsSidebar !== false,
    enableHumanTakeoverMode: chatUiPolicy?.enableHumanTakeoverMode === true,
    showTaskQueuePlannerInChat:
      chatUiPolicy?.showTaskQueuePlannerInChat === true,
    forceUserNameAsYou: chatUiPolicy?.forceUserNameAsYou === true,
    compactToolCalls: chatUiPolicy?.compactToolCalls === true,
    publicUserDisplayName: chatUiPolicy?.publicUserDisplayName || "",
  };
}

export function getClickableAgentAvatarSx(avatarClick) {
  if (!avatarClick) return {};
  return {
    cursor: "pointer",
    transition:
      "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(24, 24, 27, 0.2)",
      filter: "brightness(0.98)",
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 1px 4px rgba(24, 24, 27, 0.16)",
    },
  };
}

// Java's ZonedDateTime.toString() — used by workflow-engine for fields like
// `Project.lastModified` — appends a bracketed zone region after the offset
// (e.g. "2026-04-29T12:34:56.789Z[UTC]" or "...+10:00[Australia/Sydney]").
// That suffix is valid in Java but the JS Date constructor returns NaN for
// it, which renders as "Invalid Date" downstream. Strip the suffix before
// parsing so both pure-ISO and ZonedDateTime strings round-trip.
function parseBackendDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    return Number.isNaN(dateStr.getTime()) ? null : dateStr;
  }
  const cleaned =
    typeof dateStr === "string" ? dateStr.replace(/\[[^\]]+\]$/, "") : dateStr;
  const date = new Date(cleaned);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getSessionDayKey(dateStr) {
  const date = parseBackendDate(dateStr);
  if (!date) return "";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function countActiveScheduledItems(
  scheduledMessages = [],
  recurringSchedules = []
) {
  const activeMessages = scheduledMessages.filter(
    (message) => message.status === "SCHEDULED" || message.status === "RESUMING"
  );
  const activeRecurring = recurringSchedules.filter((schedule) =>
    ["ACTIVE", "FIRING", "PAUSED", "COMPLETED"].includes(schedule.status)
  );
  return activeMessages.length + activeRecurring.length;
}

export function formatRelativeTime(dateStr) {
  const date = parseBackendDate(dateStr);
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatSlackLastReplyTime(dateStr) {
  const date = parseBackendDate(dateStr);
  if (!date) return "";
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return `Last reply today at ${time}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Last reply yesterday at ${time}`;
  }
  const datePart = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `Last reply ${datePart} at ${time}`;
}

export function formatScheduleTime(dateStr) {
  const date = parseBackendDate(dateStr);
  if (!date) return "";
  const now = new Date();
  const diffMs = date - now;
  if (diffMs < 0) return "overdue";
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `in ${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `in ${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `in ${diffDay}d`;
}
